import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ACCOUNT_QUERY_KEYS } from '@/lib/constants/account';
import { authKeys } from '@/features/auth/queryKeys';
import { AccountApi } from '../AccountApi';
import { AccountUtils } from '../AccountUtils';
import type {
  ICardConfirmReq,
  ICardConfirmRes,
  ICardOnFileRes,
  ICardSetupRes,
  IUpgradeTrialSavedCardReq,
} from '@/lib/types/Billing';

/**
 * One SetupIntent per open card form. Modeled as a query (not a mutation) so
 * React StrictMode's double effect and re-renders share a single intent, and
 * `attempt` mints a fresh one after a consumed/expired intent or a failed call.
 * Never refetched in the background: every fetch creates a Stripe object.
 */
export function useCardSetup(attempt: number) {
  const { authData, customerInfo } = useAuth();
  const parentCId = AccountUtils.resolveParentCId(customerInfo, authData);

  return useQuery<ICardSetupRes>({
    queryKey: ACCOUNT_QUERY_KEYS.cardSetup(parentCId ?? 0, attempt),
    queryFn: () => AccountApi.cardSetup(parentCId!),
    enabled: parentCId != null,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

/** Persist the Stripe-confirmed SetupIntent as the default card. */
export function useCardConfirm() {
  const { authData, customerInfo } = useAuth();
  const queryClient = useQueryClient();
  const parentCId = AccountUtils.resolveParentCId(customerInfo, authData);

  return useMutation({
    mutationFn: (req: ICardConfirmReq) => {
      if (!parentCId) throw new Error('Unable to resolve billing account');
      return AccountApi.cardConfirm(parentCId, req);
    },
    onSuccess: (res: ICardConfirmRes) => {
      if (!parentCId) return;
      queryClient.setQueryData<ICardOnFileRes>(ACCOUNT_QUERY_KEYS.cardOnFile(parentCId), { card: res.card });
      // The intent is consumed: drop every cached setup so a reopened form mints a new one.
      queryClient.removeQueries({ queryKey: [...ACCOUNT_QUERY_KEYS.all, 'cardSetup'] });
      queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.paymentMethod(parentCId) });
      queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.billingPlan(parentCId) });
    },
  });
}

/** Trial → paid after the card was saved through setup/confirm. */
export function useUpgradeTrialWithSavedCard() {
  const { authData, customerInfo } = useAuth();
  const queryClient = useQueryClient();
  const parentCId = AccountUtils.resolveParentCId(customerInfo, authData);
  const userCId = authData?.cpuser.core.cId ?? null;

  return useMutation({
    mutationFn: (req: IUpgradeTrialSavedCardReq) => AccountApi.upgradeTrialWithSavedCard(req),
    onSuccess: () => {
      if (parentCId) {
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.billingPlan(parentCId) });
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.paymentMethod(parentCId) });
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.cardOnFile(parentCId) });
      }
      if (userCId) {
        queryClient.invalidateQueries({ queryKey: authKeys.customerInfo(userCId) });
      }
    },
  });
}
